import { toDateTime } from '@app/libs/format'
import { formatParamsFilter } from '@app/libs/formatParamsFilter'
import { Inertia } from '@inertiajs/inertia'
import { usePage } from '@inertiajs/inertia-react'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import { useTranslation } from 'react-i18next'

const FilterChip = ({ filter = [], filterForm, routeName, onFilter }) => {
  const { t } = useTranslation()
  const { url } = usePage()
  const urlObj = new URL(url, location.origin)
  const onSubmit = (values) => {
    onFilter(values)
    const checkFilter = formatParamsFilter(values)
    Inertia.visit(route(routeName, { ...checkFilter }), {
      preserveState: true,
      preserveScroll: true,
    })
  }
  switch (filter.type) {
    case 'singleSelect': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }
      const newLocal = 'categories'
      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)}: ${
                  filter.name === newLocal
                    ? filter.options.find((e) => e.id === filter.value)?.name
                    : filter.options.find((e) => e.id === filter.value)?.id
                }`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }
    case 'multichip': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }

      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)}: ${filter.options.find((item) => item.id === filter.value)?.name}`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }
    case 'multiSelect': {
      const handleDelete = (filter, valueDelete) => {
        filterForm.setValue(
          filter.name,
          filter.value.filter((item) => item !== valueDelete)
        )
        filterForm.handleSubmit(onSubmit)()
      }

      const queryList = []
      for (const param of urlObj.searchParams.keys()) {
        if (param.startsWith(filter.name) && '' !== urlObj.searchParams.get(param)) {
          queryList.push(urlObj.searchParams.get(param))
        }
      }

      if ('' === filterForm.getValues(filter.name) && queryList.length) {
        filterForm.setValue(filter.name, queryList)
      }

      if ('' === filter.value && queryList.length) {
        filter.value = queryList
      }

      return (
        <>
          {filter.value &&
            filter.value.map((multiSelectItem, index) => (
              <Grid item key={index} xs="auto">
                <Chip
                  color="primary"
                  label={`${t(filter.displayName)}:
                   ${filter.options.find((item) => item.id === multiSelectItem)?.name}`}
                  variant="outlined"
                  onDelete={() => handleDelete(filter, multiSelectItem)}
                />
              </Grid>
            ))}
        </>
      )
    }
    case 'string': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }

      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)}: ${filter.value}`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }

    case 'dateTime': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, null)
        filterForm.handleSubmit(onSubmit)()
      }
      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)}: ${toDateTime(filter.value)}`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }

    case 'number': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }
      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)} ${'bigger_than' === filter.operator_value ? '≥' : '≤'} ${
                  filter.value ?? ''
                }`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }
    case 'min_price': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }
      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)} ≥ ${filter.value ?? ''}`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }
    case 'max_price': {
      const handleDelete = (filter) => {
        filterForm.setValue(filter.name, '')
        filterForm.handleSubmit(onSubmit)()
      }
      return (
        <>
          {filter.value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={`${t(filter.displayName)} ≤ ${filter.value ?? ''}`}
                variant="outlined"
                onDelete={() => handleDelete(filter)}
              />
            </Grid>
          )}
        </>
      )
    }

    case 'dateRange': {
      const handleDelete = (filter, value) => () => {
        if (filter.start_value === value) {
          filterForm.setValue(`${filter.name}_start_at`, '')
        }

        if (filter.end_value === value) {
          filterForm.setValue(`${filter.name}_end_at`, '')
        }

        filterForm.handleSubmit(onSubmit)()
      }

      return (
        <>
          {filter.start_value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={t(filter.displayNameTranslationKey ?? 'FILTER.START_AT', {
                  time: filter.start_value && toDateTime(filter.start_value),
                })}
                variant="outlined"
                onDelete={handleDelete(filter, filter.start_value)}
              />
            </Grid>
          )}

          {filter.end_value && (
            <Grid item xs="auto">
              <Chip
                color="primary"
                label={t(filter.displayNameTranslationKey ?? 'FILTER.END_AT', {
                  time: filter.end_value && toDateTime(filter.end_value),
                })}
                variant="outlined"
                onDelete={handleDelete(filter, filter.end_value)}
              />
            </Grid>
          )}
        </>
      )
    }

    default:
      return null
  }
}

export default FilterChip
